import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Core } from "@/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@homy/ui";
import {
  Search,
  Building2,
  Key,
  Wrench,
  CheckSquare,
  Megaphone,
  UserRound,
  FileText,
  Loader2,
  CornerDownLeft,
} from "lucide-react";

const GROUP_ORDER = [
  "property",
  "rental",
  "contract",
  "application",
  "maintenance_request",
  "task",
  "listing",
  "user",
];

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { t } = useTranslation(["core", "common"]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await Core.api.searchQuery({ q: query.trim(), limit: 20 });
        setResults(res.results || []);
        setSelectedIndex(0);
      } catch (err) {
        console.error("Global search failed:", err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const groupedResults = useMemo(() => {
    const groups = {};
    results.forEach((item) => {
      const type = item.entityType || "other";
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    });
    return groups;
  }, [results]);

  const sortedGroupKeys = useMemo(() => {
    return Object.keys(groupedResults).sort((a, b) => {
      const idxA = GROUP_ORDER.indexOf(a);
      const idxB = GROUP_ORDER.indexOf(b);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [groupedResults]);

  const flatResults = useMemo(() => {
    const list = [];
    sortedGroupKeys.forEach((key) => {
      list.push(...groupedResults[key]);
    });
    return list;
  }, [sortedGroupKeys, groupedResults]);

  const handleSelect = useCallback(
    (item) => {
      if (!item) return;
      onOpenChange(false);
      const type = item.entityType;
      const id = item.entityId;

      switch (type) {
        case "property":
          navigate(`/properties/${id}`);
          break;
        case "rental":
          navigate(`/rentals/${id}`);
          break;
        case "contract":
          navigate(`/rentals/contracts/${id}`);
          break;
        case "application":
          navigate(`/applications/${id}`);
          break;
        case "maintenance_request":
          navigate(`/maintenance/${id}`);
          break;
        case "task":
          navigate(`/tasks?taskId=${id}`);
          break;
        case "listing":
          navigate(`/listings?preview=${id}`);
          break;
        case "user":
          navigate(`/settings/users/${id}`);
          break;
        default:
          break;
      }
    },
    [onOpenChange, navigate],
  );

  useEffect(() => {
    if (!open || flatResults.length === 0) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % flatResults.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(
          (prev) => (prev - 1 + flatResults.length) % flatResults.length,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (flatResults[selectedIndex]) {
          handleSelect(flatResults[selectedIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, flatResults, selectedIndex, handleSelect]);

  const getEntityIcon = (type) => {
    switch (type) {
      case "property":
        return <Building2 size={16} className="text-blue-500 shrink-0" />;
      case "rental":
        return <Key size={16} className="text-emerald-500 shrink-0" />;
      case "contract":
        return <FileText size={16} className="text-primary shrink-0" />;
      case "application":
        return <FileText size={16} className="text-purple-500 shrink-0" />;
      case "maintenance_request":
        return <Wrench size={16} className="text-amber-500 shrink-0" />;
      case "task":
        return <CheckSquare size={16} className="text-rose-500 shrink-0" />;
      case "listing":
        return <Megaphone size={16} className="text-teal-500 shrink-0" />;
      case "user":
        return <UserRound size={16} className="text-sky-500 shrink-0" />;
      default:
        return <Search size={16} className="text-muted-foreground shrink-0" />;
    }
  };

  const getGroupLabel = (type) => {
    return (
      t(`core:commandPalette.groups.${type}`) ||
      type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="command-palette-dialog"
        className="w-[calc(100vw-2rem)] sm:w-full max-w-xl max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden bg-card/95 backdrop-blur-md border border-border shadow-2xl rounded-2xl gap-0"
      >
        <DialogTitle className="sr-only">
          {t("core:commandPalette.title", "Command Palette")}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t("core:commandPalette.placeholder", "Type a command or search (⌘K)...")}
        </DialogDescription>

        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <Search size={18} className="text-muted-foreground shrink-0" />
          <input
            type="text"
            data-testid="command-palette-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(
              "core:commandPalette.placeholder",
              "Type a command or search (⌘K)...",
            )}
            aria-label={t(
              "core:commandPalette.placeholder",
              "Type a command or search (⌘K)...",
            )}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground focus:ring-0 focus:outline-none"
          />
          {loading && (
            <Loader2 size={16} className="animate-spin text-muted-foreground shrink-0" />
          )}
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-overline-size font-medium text-muted-foreground">
            {t("core:keyboard.esc", "ESC")}
          </kbd>
        </div>

        <div
          data-testid="command-palette-results"
          role="listbox"
          className="h-[min(360px,50dvh)] sm:h-[400px] max-h-[calc(100dvh-10rem)] overflow-y-auto p-2 custom-scrollbar"
        >
          {query.trim().length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              {t(
                "core:commandPalette.empty",
                "Type at least 2 characters to search...",
              )}
            </div>
          ) : query.trim().length < 2 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              {t(
                "core:commandPalette.empty",
                "Type at least 2 characters to search...",
              )}
            </div>
          ) : flatResults.length === 0 && !loading ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-muted-foreground">
              <span className="mb-1">
                {t("core:commandPalette.noResults", "No results found for")}
              </span>
              <span className="font-semibold text-foreground">
                &quot;{query}&quot;
              </span>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedGroupKeys.map((groupKey) => {
                const items = groupedResults[groupKey];
                return (
                  <div key={groupKey} className="space-y-1">
                    <div className="px-3 py-1 text-caption font-semibold uppercase tracking-wider text-muted-foreground/70">
                      {getGroupLabel(groupKey)}
                    </div>
                    {items.map((item) => {
                      const flatIndex = flatResults.indexOf(item);
                      const isSelected = flatIndex === selectedIndex;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          data-testid={`command-palette-item-${item.id}`}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setSelectedIndex(flatIndex)}
                          className={cn(
                            "w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-100 cursor-pointer min-h-[44px]",
                            isSelected
                              ? "bg-primary/12 text-foreground font-medium"
                              : "hover:bg-muted/50 text-muted-foreground hover:text-foreground",
                          )}
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {getEntityIcon(item.entityType)}
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold truncate text-foreground">
                                {item.title}
                              </div>
                              {item.content && (
                                <div className="text-xs truncate text-muted-foreground/80 mt-0.5">
                                  {item.content}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {isSelected && (
                              <CornerDownLeft
                                size={12}
                                className="text-primary/70 animate-pulse"
                              />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="hidden sm:flex items-center justify-between px-4 py-2 border-t border-border bg-muted/30 text-overline-size text-muted-foreground shrink-0 select-none">
          <div className="flex items-center gap-1.5">
            <span>
              {t("core:commandPalette.navigation", "Navigate")}
            </span>
            <kbd className="px-1 rounded bg-muted border border-border">↑↓</kbd>
            <span>
              {t("core:commandPalette.select", "Select")}
            </span>
            <kbd className="px-1 rounded bg-muted border border-border">
              {t("core:keyboard.enter", "Enter")}
            </kbd>
          </div>
          <div>
            <span>
              {t("common:actions.close", "Close")}{" "}
              <kbd className="px-1 rounded bg-muted border border-border">
                {t("core:keyboard.esc", "ESC")}
              </kbd>
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
